/**
 * API Keys Service (FEAT-033)
 *
 * Typed client for the per-user gateway API keys:
 *
 *  User (JWT, any role) -- /api/v1/me/api-keys*
 *   - GET    /me/api-keys                 listMine
 *   - POST   /me/api-keys                 createMine   (returns the secret ONCE)
 *   - POST   /me/api-keys/:id/rotate      rotateMine   (returns the secret ONCE)
 *   - DELETE /me/api-keys/:id             revokeMine
 *   - GET    /me/api-keys/:id/usage       usageMine
 *
 *  Admin (JWT, role admin) -- /api/v1/api-keys*
 *   - GET    /api-keys                    list
 *   - GET    /api-keys/policy             getPolicy
 *   - GET    /api-keys/:id                get
 *   - PATCH  /api-keys/:id                updatePolicies
 *   - POST   /api-keys/:id/suspend        suspend
 *   - POST   /api-keys/:id/reactivate     reactivate
 *   - POST   /api-keys/:id/rotate         rotate       (NO secret: the admin never sees it)
 *   - DELETE /api-keys/:id                revoke
 *   - GET    /api-keys/:id/requests       requests     (cursor pagination)
 *
 * Shapes mirror contracts/api-keys-endpoints.yaml (source of truth). Dates are ISO strings.
 *
 * Paths start at /v1 because apiClient prefixes the base URL (VITE_API_BASE_URL, default
 * '/api'), same convention as services/api/quotas.ts.
 *
 * Screens never call this module directly: they use hooks/useMyApiKeys.ts and
 * hooks/useAdminApiKeys.ts. No message translation happens here; errors propagate as
 * ApiClientError and screens map the error code to i18n keys (see ApiKeyErrorCode).
 */

import { apiClient, ApiClientError } from '../api-client';
import type { TokenUsage } from './quotas';
import type { GatewayBaseUrls } from '@/utils/gatewaySnippets';

export type { GatewayBaseUrls };

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type ApiKeyStatus = 'active' | 'suspended' | 'revoked';

/** api_keys.kind -- 'system' is the technical key of the organization (SPEC.md 7.7). */
// --- FEAT-056 TASK-006 (kind service) ---
export type ApiKeyKind = 'user' | 'system' | 'service';
// --- /FEAT-056 TASK-006 (kind service) ---

export type ApiKeyScope = 'inference' | 'management';

export type GatewayClient =
  | 'claude_code'
  | 'codex'
  | 'cursor'
  | 'openai_sdk'
  | 'anthropic_sdk'
  | 'curl'
  | 'other';

export type GatewayEndpointTag =
  | 'messages'
  | 'count_tokens'
  | 'chat_completions'
  | 'responses'
  | 'models'
  | 'embeddings'
  | 'me'
  | 'other';

export type GatewaySurface = 'chat' | 'code' | 'code_agent' | 'external' | 'system';

/** Usage period of GET /me/api-keys/:id/usage. Also the value of ApiKeyUsage.period.label. */
export type ApiKeyUsageRange = '7d' | '30d' | '90d';

/**
 * E_* error codes of the api-keys endpoints (contracts/api-keys-endpoints.yaml error_codes).
 * Screens map these to i18n keys (design.md C.9 and F.8). E_VALIDATION and HTTP 429 are
 * generic codes handled by the screens too, so they are not part of this union.
 */
export type ApiKeyErrorCode =
  | 'E_API_KEY_POLICY_DENIED'
  | 'E_API_KEY_LIMIT_REACHED'
  | 'E_API_KEY_DEFAULT_PROTECTED'
  | 'E_API_KEY_INVALID_STATE'
  | 'E_API_KEY_NOT_FOUND'
  | 'E_API_KEY_SYSTEM_PROTECTED';

export const API_KEY_ERROR_CODES: readonly ApiKeyErrorCode[] = [
  'E_API_KEY_POLICY_DENIED',
  'E_API_KEY_LIMIT_REACHED',
  'E_API_KEY_DEFAULT_PROTECTED',
  'E_API_KEY_INVALID_STATE',
  'E_API_KEY_NOT_FOUND',
  'E_API_KEY_SYSTEM_PROTECTED',
];

/**
 * Returns the api-key error code carried by an error thrown by the hooks, or null.
 * Pure extraction -- no translation. Reads ApiClientError.code.
 */
export function apiKeyErrorCode(err: unknown): ApiKeyErrorCode | null {
  if (!(err instanceof ApiClientError) || typeof err.code !== 'string') return null;
  return (API_KEY_ERROR_CODES as readonly string[]).includes(err.code)
    ? (err.code as ApiKeyErrorCode)
    : null;
}

// ---------------------------------------------------------------------------
// Shared schemas
// ---------------------------------------------------------------------------

/**
 * Access window. days are ISO weekdays 1..7 (1 = Monday, 7 = Sunday); from/until are
 * "HH:mm" in tz; from > until crosses midnight. null = no window.
 */
export interface AccessSchedule {
  days: number[];
  from: string;
  until: string;
  tz: string;
}

/** Technical per-key policies. null in any field = no restriction / inherit. */
export interface ApiKeyPolicies {
  allowed_models: string[] | null;
  allowed_endpoints: GatewayEndpointTag[] | null;
  rate_limit_rpm: number | null;
  rate_limit_rpd: number | null;
  max_concurrent: number | null;
  ip_allowlist: string[] | null;
  access_schedule: AccessSchedule | null;
}

export interface ApiKeyLastRotation {
  by: 'user' | 'admin';
  at: string;
  by_user_id: string;
}

/** Public key object (SPEC.md 5.1). Never contains the secret nor key_hash. */
export interface ApiKeyPublic {
  id: string;
  name: string;
  /** oia_ + 8 chars (12 total). The only part of the secret that is ever displayed. */
  key_prefix: string;
  is_default: boolean;
  kind: ApiKeyKind;
  status: ApiKeyStatus;
  scopes: ApiKeyScope[];
  created_at: string;
  rotated_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  last_used_client: GatewayClient | null;
  last_used_ip: string | null;
  last_rotation: ApiKeyLastRotation | null;
  policies: ApiKeyPolicies;
  // --- FEAT-056 TASK-007 (created_by_admin) ---
  /** True when an administrator created this key for the user (delivered by the API since FEAT-056 TASK-004; optional so older fixtures still type-check). */
  created_by_admin?: boolean;
  // --- /FEAT-056 TASK-007 (created_by_admin) ---
}

export interface AdminApiKeyOwner {
  id: string;
  /** 'Sistema' for the technical user (kind = system). */
  display_name: string;
  email: string;
  role: 'admin' | 'maintainer' | 'member';
  status: 'active' | 'invited' | 'disabled' | 'locked' | 'system' | 'service';
}

export interface AdminApiKeyTeam {
  id: string;
  name: string;
}

export interface ApiKeySuspension {
  reason: string | null;
  at: string;
  by_user_id: string;
}

export interface ApiKeyRevocation {
  reason: string | null;
  at: string;
  by_user_id: string | null;
}

/** ApiKeyPublic + owner, teams, suspension and revocation. */
export interface AdminApiKeyRow extends ApiKeyPublic {
  user: AdminApiKeyOwner;
  teams: AdminApiKeyTeam[];
  suspension: ApiKeySuspension | null;
  revocation: ApiKeyRevocation | null;
  /**
   * Active keys of kind 'user' owned by the key's user, default included (the N of the admin's
   * "N of M"). null on rows of kind 'system' (the limit does not apply to the technical key).
   */
  user_active_keys: number | null;
  // --- FEAT-056 TASK-006 (service key owner) ---
  /** Service keys: the responsible person (`user` is then the service account). Null elsewhere. */
  owner?: AdminApiKeyServiceOwner | null;
  /** Service keys: the team chosen for cost and quota. */
  team?: AdminApiKeyTeam | null;
  /** Service keys: the owner is missing or not active (the key keeps working). */
  owner_inactive?: boolean;
  // --- /FEAT-056 TASK-006 (service key owner) ---
}

// --- FEAT-056 TASK-006 (service key owner) ---
export interface AdminApiKeyServiceOwner {
  id: string;
  display_name: string;
  active: boolean;
}
// --- /FEAT-056 TASK-006 (service key owner) ---

/** Organization key policy (GET /api-keys/policy). */
export interface KeyPolicy {
  max_keys_per_user: number;
  default_expiry_days: number | null;
  require_ip_allowlist: boolean;
  allowed_scopes: ApiKeyScope[];
}

/** What the org policy allows for the calling user (GET /me/api-keys). */
export interface MyKeyPolicy {
  /** Active keys of kind user of the user (default included, suspended do not count) < max_keys_per_user */
  can_create_additional: boolean;
  max_keys_per_user: number;
  default_expiry_days: number | null;
}

export interface ApiKeyUsagePeriod {
  /** inclusive */
  from: string;
  /** exclusive */
  to: string;
  /** The range value itself, never translated text -- the app translates it. */
  label: ApiKeyUsageRange;
}

export interface ApiKeyUsageBySurface {
  surface: GatewaySurface;
  requests: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
}

export interface ApiKeyUsage {
  period: ApiKeyUsagePeriod;
  requests: number;
  tokens_in: number;
  tokens_in_cache_read: number;
  tokens_in_cache_creation: number;
  tokens_out: number;
  cost_usd: number;
  by_surface: ApiKeyUsageBySurface[];
  last_used_at: string | null;
  last_used_client: string | null;
  /** Item for key 'all' of the user's quotas, or null. */
  quota: TokenUsage | null;
  source: 'cost_records';
}

/** Row of GET /api-keys/:id/requests (contract GatewayRequestRow). */
export interface ApiKeyRequestRow {
  id: string;
  started_at: string;
  status: string;
  http_status: number | null;
  endpoint: string;
  requested_model: string | null;
  provider_id: string | null;
  surface: string;
  client: string | null;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  latency_ms: number | null;
  error_code: string | null;
  blocked_reason: string | null;
}

// ---------------------------------------------------------------------------
// Requests and responses
// ---------------------------------------------------------------------------

export interface CreateApiKeyInput {
  /** 2..80 chars */
  name: string;
}

/**
 * PATCH /api-keys/:id -- every field optional (at least one); null clears it
 * (except name, which cannot be cleared).
 */
export interface UpdateApiKeyPoliciesInput {
  name?: string;
  allowed_models?: string[] | null;
  allowed_endpoints?: GatewayEndpointTag[] | null;
  rate_limit_rpm?: number | null;
  rate_limit_rpd?: number | null;
  max_concurrent?: number | null;
  ip_allowlist?: string[] | null;
  access_schedule?: AccessSchedule | null;
  expires_at?: string | null;
  // --- FEAT-056 TASK-006 (change owner) ---
  /** Service keys only (422 otherwise). */
  owner_user_id?: string;
  team_id?: string | null;
  // --- /FEAT-056 TASK-006 (change owner) ---
}

export interface AdminApiKeysParams {
  user_id?: string;
  team_id?: string;
  status?: ApiKeyStatus;
  client?: GatewayClient;
  /** ILIKE on display_name, email, key_prefix (fewer than 2 chars is ignored by the API). */
  q?: string;
  is_default?: boolean;
  // --- FEAT-056 TASK-006 (kind filter) ---
  kind?: ApiKeyKind;
  // --- /FEAT-056 TASK-006 (kind filter) ---
  page?: number;
  per_page?: number;
}

export interface ApiKeyRequestsParams {
  cursor?: string | null;
  limit?: number;
}


export interface MyApiKeysResponse {
  data: ApiKeyPublic[];
  policy: MyKeyPolicy;
  /**
   * Gateway bases composed server-side (D32 addendum, FEAT-035 TASK-012) -- the ONLY input of
   * the connection snippets; the browser never composes them.
   */
  base_urls: GatewayBaseUrls;
}

/**
 * Response of createMine / rotateMine. The secret is shown ONCE: it must never reach the
 * TanStack Query cache nor any global/persistent state (see hooks/useMyApiKeys.ts).
 */
export interface ApiKeyWithSecret {
  key: ApiKeyPublic;
  secret: string;
}

// --- FEAT-056 TASK-006 (admin create) ---
/** AdminCreateKeyPolicies of contracts/api-keys-endpoints.yaml (null = no restriction). */
export interface AdminCreateKeyPolicies {
  /** target=user only. */
  allowed_models?: string[] | null;
  allowed_endpoints?: GatewayEndpointTag[] | null;
  rate_limit_rpm?: number | null;
  rate_limit_rpd?: number | null;
  max_concurrent?: number | null;
  ip_allowlist?: string[] | null;
  access_schedule?: AccessSchedule | null;
}

/** POST /api-keys body, discriminated by `target`. */
export type AdminCreateKeyInput =
  | {
      target: 'service';
      name: string;
      owner_user_id: string;
      team_id?: string;
      model_ids: string[];
      policies?: AdminCreateKeyPolicies;
      /** ISO; omitted = 90 days; null = never. */
      expires_at?: string | null;
    }
  | {
      target: 'user';
      user_id: string;
      name: string;
      policies?: AdminCreateKeyPolicies;
      /** ISO; omitted = organization default; null = never. */
      expires_at?: string | null;
    };

/**
 * 201 of POST /api-keys and 200 of POST /api-keys/:id/rotate on a service key. The secret is shown
 * ONCE: it must never reach the TanStack Query cache (see hooks/useAdminServiceKeys.ts).
 */
export interface AdminKeyWithSecret {
  key: AdminApiKeyRow;
  secret: string;
  base_urls: GatewayBaseUrls;
}
// --- /FEAT-056 TASK-006 (admin create) ---

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface CursorPage<T> {
  data: T[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface KeyPolicyResponse {
  policy: KeyPolicy;
  source: 'defaults' | 'ai_settings';
}

export interface AdminApiKeyResponse {
  key: AdminApiKeyRow;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Query string without undefined/null/empty values (the API treats `status=` as absent anyway). */
function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(name, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

function keyPath(base: string, id: string): string {
  return `${base}/${encodeURIComponent(id)}`;
}

const ME_BASE = '/v1/me/api-keys';
const ADMIN_BASE = '/v1/api-keys';

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const apiKeysApi = {
  // ----- user --------------------------------------------------------------

  /** GET /api/v1/me/api-keys -- my keys (revoked excluded) + what the org policy allows. */
  listMine: (): Promise<MyApiKeysResponse> => apiClient.get<MyApiKeysResponse>(ME_BASE),

  /** POST /api/v1/me/api-keys -- 201 {key, secret}. The secret is returned ONCE. */
  createMine: (input: CreateApiKeyInput): Promise<ApiKeyWithSecret> =>
    apiClient.post<ApiKeyWithSecret>(ME_BASE, input),

  /** POST /api/v1/me/api-keys/:id/rotate -- {key, secret}. The old secret stops working. */
  rotateMine: (id: string): Promise<ApiKeyWithSecret> =>
    apiClient.post<ApiKeyWithSecret>(`${keyPath(ME_BASE, id)}/rotate`),

  /** DELETE /api/v1/me/api-keys/:id -- 204. Additional keys only (default is protected). */
  revokeMine: (id: string): Promise<void> => apiClient.delete<void>(keyPath(ME_BASE, id)),

  /** GET /api/v1/me/api-keys/:id/usage?range= */
  usageMine: (id: string, range: ApiKeyUsageRange = '30d'): Promise<ApiKeyUsage> =>
    apiClient.get<ApiKeyUsage>(`${keyPath(ME_BASE, id)}/usage${qs({ range })}`),

  // ----- admin -------------------------------------------------------------

  /** GET /api/v1/api-keys -- all keys of the organization (offset pagination). */
  list: (params: AdminApiKeysParams = {}): Promise<Paginated<AdminApiKeyRow>> =>
    apiClient.get<Paginated<AdminApiKeyRow>>(`${ADMIN_BASE}${qs({ ...params })}`),

  /** GET /api/v1/api-keys/policy -- organization key policy (read only here). */
  getPolicy: (): Promise<KeyPolicyResponse> =>
    apiClient.get<KeyPolicyResponse>(`${ADMIN_BASE}/policy`),

  /** GET /api/v1/api-keys/:id */
  get: (id: string): Promise<AdminApiKeyResponse> =>
    apiClient.get<AdminApiKeyResponse>(keyPath(ADMIN_BASE, id)),

  /** PATCH /api/v1/api-keys/:id -- name and technical policies. */
  updatePolicies: (id: string, patch: UpdateApiKeyPoliciesInput): Promise<AdminApiKeyResponse> =>
    apiClient.patch<AdminApiKeyResponse>(keyPath(ADMIN_BASE, id), patch),

  /** POST /api/v1/api-keys/:id/suspend */
  suspend: (id: string, reason?: string): Promise<AdminApiKeyResponse> =>
    apiClient.post<AdminApiKeyResponse>(
      `${keyPath(ADMIN_BASE, id)}/suspend`,
      reason ? { reason } : {},
    ),

  /** POST /api/v1/api-keys/:id/reactivate */
  reactivate: (id: string): Promise<AdminApiKeyResponse> =>
    apiClient.post<AdminApiKeyResponse>(`${keyPath(ADMIN_BASE, id)}/reactivate`),

  /** POST /api/v1/api-keys/:id/rotate -- NO secret in the response (the owner must rotate). */
  rotate: (id: string): Promise<AdminApiKeyResponse> =>
    apiClient.post<AdminApiKeyResponse>(`${keyPath(ADMIN_BASE, id)}/rotate`),

  /** DELETE /api/v1/api-keys/:id -- 204; optional body {reason}. */
  revoke: (id: string, reason?: string): Promise<void> =>
    apiClient.delete<void>(
      keyPath(ADMIN_BASE, id),
      reason ? { body: JSON.stringify({ reason }) } : undefined,
    ),

  // --- FEAT-056 TASK-006 (admin create) ---
  /** POST /api/v1/api-keys -- 201 {key, secret, base_urls}, no-store. The secret is returned ONCE. */
  createAdmin: (input: AdminCreateKeyInput): Promise<AdminKeyWithSecret> =>
    apiClient.post<AdminKeyWithSecret>(ADMIN_BASE, input),

  /** POST /api/v1/api-keys/:id/rotate on a SERVICE key -- {key, secret, base_urls}. */
  rotateService: (id: string): Promise<AdminKeyWithSecret> =>
    apiClient.post<AdminKeyWithSecret>(`${keyPath(ADMIN_BASE, id)}/rotate`),
  // --- /FEAT-056 TASK-006 (admin create) ---

  /** GET /api/v1/api-keys/:id/requests?cursor&limit -- gateway requests of the key. */
  requests: (id: string, params: ApiKeyRequestsParams = {}): Promise<CursorPage<ApiKeyRequestRow>> =>
    apiClient.get<CursorPage<ApiKeyRequestRow>>(
      `${keyPath(ADMIN_BASE, id)}/requests${qs({ cursor: params.cursor, limit: params.limit })}`,
    ),
};
